import React, { useEffect, useState } from 'react';
import { Col, Row, Form, Button, Container, Card, Alert } from 'react-bootstrap';
import axios from 'axios';
// import { faTrash } from '@fortawesome/free-solid-svg-icons'; // Importing trash icon
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API } from '../../config/api';
const API_URL = `${API.BACKEND}/api/v1/standard`;

const PHASE_OPTIONS = [
  { label: 'FUNIE CUPRU', value: 'pre_stranding' },
  { label: 'IZOLARE', value: 'conductor_isolation' },
  { label: 'INFUNIERE', value: 'rope' },
  { label: 'MANTA INTERIOARA', value: 'interior_sheat' },
  { label: 'ARMARE', value: 'armouring' },
  { label: 'MANTA EXTERIOARA', value: 'exterior_sheat' }
];
const getColorHex = (colorName = '') => {
  const map = {
    BLUE: '#1976d2',
    BROWN: '#795548',
    'GREEN-YELLOW': '#9acd32',
    BLACK: '#000000',
    GREY: '#9e9e9e'
  };
  return map[colorName.toUpperCase()] || '#cccccc';
};

const PI = Math.PI;
const disc = d => (PI * Number(d) * Number(d)) / 4;
const round4 = n => Number(Number(n).toFixed(4));
const fmt2 = n => (Number.isFinite(Number(n)) ? Number(n).toFixed(2) : '—');

// Per-phase prediction readout: shows the cascade-expected Ø / section / fillingSection
// under a phase's diameter field. Green when the stored value matches the prediction;
// stored (green) → expected (red) + [Acceptă] when they diverge (stale after an
// upstream change); a plain suggestion + [Folosește] when the field is still empty.
const PhasePrediction = ({ stored, pred, onAccept }) => {
  if (!pred || !(pred.diameter > 0)) return null;
  const s = Number(stored) || 0;
  const hasStored = s > 0;
  const diff = hasStored && Math.abs(s - pred.diameter) > 0.01;
  return (
    <div className="mt-1 p-2 rounded border" style={{ background: 'rgba(0,0,0,0.02)', fontSize: 11 }}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span className="text-600 fw-bold">Previziune Ø:</span>
        {hasStored && !diff && <span className="text-success fw-bold">✓ {fmt2(s)} mm</span>}
        {diff && (
          <>
            <span className="text-success fw-bold">{fmt2(s)}</span>
            <span className="text-700">→</span>
            <span className="text-danger fw-bold">{fmt2(pred.diameter)} mm</span>
            <Button size="sm" variant="danger" className="py-0 px-2" style={{ fontSize: 10 }} onClick={onAccept}>Acceptă</Button>
          </>
        )}
        {!hasStored && (
          <>
            <span className="text-primary fw-bold">{fmt2(pred.diameter)} mm</span>
            <Button size="sm" variant="outline-primary" className="py-0 px-2" style={{ fontSize: 10 }} onClick={onAccept}>Folosește</Button>
          </>
        )}
      </div>
      <div className="text-500 mt-1">
        Secțiune {fmt2(pred.section)} mm²
        {pred.fillingSection != null && <> · Filling {fmt2(pred.fillingSection)} mm²</>}
      </div>
    </div>
  );
};

const CreateNewStandard = () => {
  const [formData, setFormData] = useState({
    name: '',
    numberOfConductors: '',
    section: '',
    hasMixedConductorSections: false, // <-- NOU
    greenYellowSection: '', // <-- NOU
    productStandard: '',
    performanceStandard: '',
    notifiedBody: '',
    dop: '',
    tension: '',
    fireReaction: '',
    color: '',
    phases: []
  });

  const [newPhase, setNewPhase] = useState('');

  const [preStranding, setPreStranding] = useState({});
  const [preStandingFormVisible, setPreStrandingFormVisible] = useState(true);

  const [conductorIsolation, setConductorIsolation] = useState({
    wire: {},
    isolation: {}
  });

  const [conductorIsolationFormVisible, setConductorIsolationFormVisible] =
    useState(true);

  const calculateSection = diameter => {
    const d = Number(diameter);
    if (!Number.isFinite(d) || d <= 0) return 0;
    return Number(((Math.PI * d * d) / 4).toFixed(4));
  };

  // find the previous phase's section (the phase this one depends on)
  const getPreviousPhaseSection = phaseIndex => {
    const phase = formData.phases[phaseIndex];
    if (!phase) return 0;

    // look for dependsOn phase
    if (phase.dependsOn) {
      const dep = formData.phases.find(p => p.name === phase.dependsOn);
      if (dep?.section) return Number(dep.section);
    }

    // fallback: use phase just before this one
    for (let i = phaseIndex - 1; i >= 0; i--) {
      const prev = formData.phases[i];
      if (prev?.section) return Number(prev.section);
    }
    return 0;
  };

  // Σ disc(coreØ) — total insulated-conductor area the bedding sheath actually
  // displaces. Unlike disc(rope) (which treats the twisted rope as a solid
  // cylinder), this keeps the plastic that fills the interstitial voids BETWEEN
  // the round conductors counted as sheath material.
  const getConductorDiscSum = () => {
    const ci = formData.phases.find(p => p.name === 'conductor_isolation');
    const list = Array.isArray(ci?.conductors) ? ci.conductors : [];
    if (!list.length) return 0;
    const discOf = c => {
      const d = Number(c?.isolation?.diameter) || 0;
      return d > 0 ? (Math.PI * d * d) / 4 : 0;
    };
    const nc = Number(formData.numberOfConductors) || 0;
    // flat shape (one representative conductor) → scale by the conductor count
    if (list.length === 1 && nc > 1) return nc * discOf(list[0]);
    return list.reduce((s, c) => s + discOf(c), 0);
  };

  // the sheath sitting directly on the rope (bedding) — interior_sheat if present,
  // else exterior_sheat. It fills the voids between conductors, so its fillingSection
  // = disc(sheathØ) − Σ disc(coreØ), not the thin annulus disc(sheathØ) − disc(ropeØ).
  const isBeddingSheath = phaseIndex => {
    const phase = formData.phases[phaseIndex];
    if (!phase || (phase.name !== 'interior_sheat' && phase.name !== 'exterior_sheat')) return false;
    if (phase.dependsOn === 'rope') return true;
    for (let i = phaseIndex - 1; i >= 0; i--) {
      const prev = formData.phases[i];
      if (prev?.section) return prev.name === 'rope';
    }
    return false;
  };

  // section + fillingSection for a phase at a given outer diameter. The bedding
  // sheath displaces Σ disc(coreØ) (interstitial fill); every other layer displaces
  // the previous phase's section (annulus / insulation-over-copper), unchanged.
  const computePhaseDerived = (phaseIndex, diameter) => {
    const d = Number(diameter);
    if (!Number.isFinite(d) || d <= 0) return { section: 0, fillingSection: 0 };
    const section = Number(((Math.PI * d * d) / 4).toFixed(4));
    const displaced = isBeddingSheath(phaseIndex)
      ? getConductorDiscSum()
      : getPreviousPhaseSection(phaseIndex);
    const fillingSection = displaced > 0 ? Number(Math.max(0, section - displaced).toFixed(4)) : 0;
    return { section, fillingSection };
  };

  // average of two radial tolerance bounds (falls back to whichever is set)
  const avgRadial = (min, max) => {
    const mn = Number(min) || 0;
    const mx = Number(max) || 0;
    if (mn && mx) return (mn + mx) / 2;
    return mn || mx || 0;
  };

  // ── Root cascade prediction (like Standard Analytics) ──
  // Expected outer Ø for each phase, each derived from the previous phase's EXPECTED
  // Ø (not the stored one). Rooted at the conductor isolation (wire Ø + insulation
  // wall) → rope packing → sheath walls → armour build. pre_stranding stays manual.
  const buildPrediction = () => {
    const phases = formData.phases;
    const pred = {};
    const ci = phases.find(p => p.name === 'conductor_isolation');
    const cond = Array.isArray(ci?.conductors) ? ci.conductors : [];
    const nc = Number(formData.numberOfConductors) || cond.length || 1;

    // expected insulated-conductor Ø per conductor (wire Ø + 2×wall); stored fallback
    const coreOuters = cond
      .map(c => {
        const wireD = Number(c?.wire?.diameter) || 0;
        const r = avgRadial(c?.isolation?.minRadial, c?.isolation?.maxRadial);
        return wireD > 0 && r > 0 ? round4(wireD + 2 * r) : Number(c?.isolation?.diameter) || 0;
      })
      .filter(d => d > 0);

    const expIsoMax = coreOuters.length ? Math.max(...coreOuters) : 0;
    const sumCoreDiscs = !coreOuters.length
      ? 0
      : coreOuters.length === 1 && nc > 1
        ? nc * disc(coreOuters[0])
        : coreOuters.reduce((s, d) => s + disc(d), 0);

    // circumscribed-circle packing ratio for n conductors in a ring
    const packRatio = n =>
      n <= 1 ? 1
        : n === 2 ? 2
          : n === 3 ? 1 + 1 / Math.sin(Math.PI / 3)
            : n === 4 ? 1 + Math.SQRT2
              : n === 5 ? 1 + 1 / Math.sin(Math.PI / 5)
                : 1 + 1 / Math.sin(Math.PI / n);

    let prevD = expIsoMax;              // running expected outer Ø
    let prevLayer = 'conductor_isolation';
    for (let i = 0; i < phases.length; i++) {
      const ph = phases[i];
      if (ph.name === 'rope') {
        const d = expIsoMax > 0 ? round4(expIsoMax * packRatio(nc)) : 0;
        pred[i] = { diameter: d, section: round4(disc(d)) };
        if (d > 0) { prevD = d; prevLayer = 'rope'; }
      } else if (ph.name === 'interior_sheat' || ph.name === 'exterior_sheat') {
        const r = avgRadial(ph.minRadial, ph.maxRadial);
        const d = prevD > 0 && r > 0 ? round4(prevD + 2 * r) : 0;
        const section = round4(disc(d));
        // bedding sheath (directly on rope) fills the interstitial voids → Σ disc(cores)
        const bedding = prevLayer === 'rope';
        const filling = d > 0 ? round4(Math.max(0, section - (bedding ? sumCoreDiscs : disc(prevD)))) : 0;
        pred[i] = { diameter: d, section, fillingSection: filling };
        if (d > 0) { prevD = d; prevLayer = ph.name; }
      } else if (ph.name === 'armouring') {
        const t = Number(ph.thickness) || 0;
        const d = prevD > 0 && t > 0 ? round4(prevD + 2 * t) : 0;
        pred[i] = { diameter: d, section: round4(disc(d)) };
        if (d > 0) { prevD = d; prevLayer = 'armouring'; }
      }
    }
    return { pred, expIsoMax, sumCoreDiscs };
  };

  // apply the cascade prediction to one phase (Ø + section + fillingSection)
  const applyPrediction = phaseIndex => {
    const p = buildPrediction().pred[phaseIndex];
    if (!p || !(p.diameter > 0)) return;
    setFormData(prev => {
      const phases = [...prev.phases];
      phases[phaseIndex] = {
        ...phases[phaseIndex],
        diameter: p.diameter,
        section: p.section,
        ...(p.fillingSection != null ? { fillingSection: p.fillingSection } : {})
      };
      return { ...prev, phases };
    });
  };

  // apply the full cascade to every predictable phase at once
  const applyAllPredictions = () => {
    const { pred } = buildPrediction();
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.map((ph, i) => {
        const p = pred[i];
        if (!p || !(p.diameter > 0)) return ph;
        return {
          ...ph,
          diameter: p.diameter,
          section: p.section,
          ...(p.fillingSection != null ? { fillingSection: p.fillingSection } : {})
        };
      })
    }));
  };

  // count phases whose stored Ø no longer matches the cascade (stale / incompatible)
  const countStalePhases = predObj => {
    const { pred } = predObj;
    return formData.phases.filter((ph, i) => {
      const p = pred[i];
      return p && p.diameter > 0 && Number(ph.diameter) > 0 && Math.abs(Number(ph.diameter) - p.diameter) > 0.01;
    }).length;
  };

  // ── Diameter estimation formulas ──

  // conductor isolation: wire.diameter + 2 × average(minRadial, maxRadial)
  const estimateIsolationDiameter = (wireDiameter, minRadial, maxRadial) => {
    const d = Number(wireDiameter);
    const min = Number(minRadial);
    const max = Number(maxRadial);
    if (!d || (!min && !max)) return null;
    const avgRadial = (min && max) ? (min + max) / 2 : (min || max);
    return Number((d + 2 * avgRadial).toFixed(4));
  };

  // (rope / sheath / armour diameter suggestions now come from the root cascade
  //  engine buildPrediction(); the conductor-isolation estimate above is the root
  //  input and is kept in its own entry form.)

  const removeConductor = (phaseIndex, conductorIndex) => {
    setFormData(prev => {
      const updatedPhases = [...prev.phases];
      if (updatedPhases[phaseIndex] && Array.isArray(updatedPhases[phaseIndex].conductors)) {
        updatedPhases[phaseIndex] = {
          ...updatedPhases[phaseIndex],
          conductors: updatedPhases[phaseIndex].conductors.filter((_, i) => i !== conductorIndex)
        };
      }
      return { ...prev, phases: updatedPhases };
    });
  };

  const handleRemovePhase = index => {
    setFormData(prev => ({
      ...prev,
      phases: prev.phases.filter((_, i) => i !== index)
    }));
  };
  const removeRope = (phaseIndex, ropeIndex) => {
    setFormData(prev => {
      const updatedPhases = [...prev.phases];

      if (
        updatedPhases[phaseIndex] &&
        Array.isArray(updatedPhases[phaseIndex].rope)
      ) {
        updatedPhases[phaseIndex].rope = updatedPhases[phaseIndex].rope.filter(
          (_, i) => i !== ropeIndex
        );
      }

      return {
        ...prev,
        phases: updatedPhases
      };
    });
  };
  const handleChange = e => {
    const { name, value } = e.target;
    const numericFields = ['numberOfConductors', 'section', 'greenYellowSection'];

    if (numericFields.includes(name)) {
      setFormData(prev => ({ ...prev, [name]: Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: String(value).toUpperCase() }));
    }
  };

  // Fields that should always be stored as strings (uppercase)
  const STRING_FIELDS = new Set(['type', 'material', 'color', 'isolationMaterial', 'isolationType']);
  // Fields that are comma-separated arrays of numbers
  const ARRAY_FIELDS = new Set(['numberOfWires', 'wireDiameter']);

  const handlePhaseChange = (index, key, value) => {
    const updatedPhases = [...formData.phases];
    const phase = updatedPhases[index];

    if (phase.name === 'conductor_isolation') {
      if (!phase.wire) phase.wire = {};
      if (!phase.isolation) phase.isolation = {};

      // Wire fields
      const wireKeys = ['density', 'wireSection', 'wireDiameter', 'wireRopeDiameter', 'wireRopeSection',
        'diameter', 'section', 'type', 'material', 'numberOfWires'];

      if (wireKeys.includes(key)) {
        if (ARRAY_FIELDS.has(key)) {
          // always store as Number[]
          phase.wire[key] = String(value).split(',').map(s => Number(s.trim())).filter(n => Number.isFinite(n));
        } else if (key === 'material' || key === 'type') {
          phase.wire[key] = String(value).toUpperCase();
        } else if (key === 'wireRopeDiameter') {
          phase.wire.diameter = Number(value);
        } else if (key === 'wireRopeSection') {
          phase.wire.section = Number(value);
        } else {
          phase.wire[key] = Number(value);
        }
      }
      // Isolation fields
      else if (key === 'isolationDiameter') phase.isolation.diameter = Number(value);
      else if (key === 'isolationMaterial') phase.isolation.material = String(value).toUpperCase();
      else if (key === 'densityMaterial') phase.isolation.density = Number(value);
      else if (key === 'isolationSection') phase.isolation.section = Number(value);
      else if (key === 'isolationFillingSection') phase.isolation.fillingSection = Number(value);
      else if (key === 'isolationMinRadial') phase.isolation.minRadial = Number(value);
      else if (key === 'isolationMaxRadial') phase.isolation.maxRadial = Number(value);
      else if (key === 'isolationType') phase.isolation.type = String(value).toUpperCase();
      else if (key === 'color') phase.isolation.color = String(value).toUpperCase();
      else phase.isolation[key] = String(value).toUpperCase();
    } else {
      // Generic phase fields
      if (ARRAY_FIELDS.has(key)) {
        phase[key] = String(value).split(',').map(s => Number(s.trim())).filter(n => Number.isFinite(n));
      } else if (STRING_FIELDS.has(key)) {
        phase[key] = String(value).toUpperCase();
      } else {
        phase[key] = Number(value);
      }
    }

    setFormData(prev => ({ ...prev, phases: updatedPhases }));
  };

  const handlePreStrandingChange = (key, value) => {
    setPreStranding(prev => ({
      ...prev,
      [key]: value
    }));
  };

  useEffect(() => {
    console.log('Form Data Updated:', formData);
    // Set initial phase data if the first phase is 'pre_stranding' )
    console.log('pre conductorIsolation phase ', conductorIsolation);
    console.log('pre pre_strading phase ', preStranding);
  }, [formData, conductorIsolation]);

  const savePhase = (phaseName, index) => {
    setFormData(prev => {
      const updatedPhases = [...prev.phases];

      if (
        updatedPhases[index] &&
        updatedPhases[index].name === 'pre_stranding' &&
        Array.isArray(updatedPhases[index].rope)
      ) {
        // normalize pre_stranding rope entry — ensure all numbers
        const normalizedRope = {
          material: String(preStranding.material || 'COPPER').toUpperCase(),
          type: String(preStranding.type || 'MULTI-WIRE').toUpperCase(),
          density: Number(preStranding.density) || 8.96,
          numberOfDrums: preStranding.numberOfDrums ? Number(preStranding.numberOfDrums) : null,
          numberOfWires: Array.isArray(preStranding.numberOfWires)
            ? preStranding.numberOfWires.map(n => Number(n)).filter(Number.isFinite)
            : typeof preStranding.numberOfWires === 'string'
              ? preStranding.numberOfWires.split(',').map(s => Number(s.trim())).filter(Number.isFinite)
              : [Number(preStranding.numberOfWires)].filter(Number.isFinite),
          wireDiameter: Array.isArray(preStranding.wireDiameter)
            ? preStranding.wireDiameter.map(n => Number(n)).filter(Number.isFinite)
            : typeof preStranding.wireDiameter === 'string'
              ? preStranding.wireDiameter.split(',').map(s => Number(s.trim())).filter(Number.isFinite)
              : [Number(preStranding.wireDiameter)].filter(Number.isFinite),
          diameter: Number(preStranding.diameter) || null,
        };

        updatedPhases[index].rope.push(normalizedRope);
        setPreStrandingFormVisible(false);
        setPreStranding({
          material: '',
          type: '',
          density: '',
          numberOfDrums: '',
          numberOfWires: [],
          wireDiameter: []
        });
      }

      if (phaseName === 'conductor_isolation') {
        // normalize conductor — ensure all numbers
        const normalizedConductor = {
          wire: {
            material: String(conductorIsolation.wire?.material || 'COPPER').toUpperCase(),
            type: String(conductorIsolation.wire?.type || 'SINGLE-WIRE').toUpperCase(),
            density: Number(conductorIsolation.wire?.density) || 8.96,
            diameter: Number(conductorIsolation.wire?.diameter) || null,
            section: Number(conductorIsolation.wire?.section) || null,
            numberOfWires: Array.isArray(conductorIsolation.wire?.numberOfWires)
              ? conductorIsolation.wire.numberOfWires.map(n => Number(n)).filter(Number.isFinite)
              : typeof conductorIsolation.wire?.numberOfWires === 'string'
                ? conductorIsolation.wire.numberOfWires.split(',').map(s => Number(s.trim())).filter(Number.isFinite)
                : [Number(conductorIsolation.wire?.numberOfWires)].filter(Number.isFinite),
            ...(conductorIsolation.wire?.wireDiameter !== undefined ? {
              wireDiameter: Array.isArray(conductorIsolation.wire.wireDiameter)
                ? conductorIsolation.wire.wireDiameter.map(n => Number(n)).filter(Number.isFinite)
                : typeof conductorIsolation.wire.wireDiameter === 'string'
                  ? conductorIsolation.wire.wireDiameter.split(',').map(s => Number(s.trim())).filter(Number.isFinite)
                  : [Number(conductorIsolation.wire.wireDiameter)].filter(Number.isFinite)
            } : {}),
          },
          isolation: {
            material: String(conductorIsolation.isolation?.material || 'PVC').toUpperCase(),
            type: String(conductorIsolation.isolation?.type || '').toUpperCase(),
            density: Number(conductorIsolation.isolation?.density) || null,
            diameter: Number(conductorIsolation.isolation?.diameter) || null,
            section: Number(conductorIsolation.isolation?.section) || null,
            fillingSection: Number(conductorIsolation.isolation?.fillingSection) || null,
            minRadial: Number(conductorIsolation.isolation?.minRadial) || null,
            maxRadial: Number(conductorIsolation.isolation?.maxRadial) || null,
            color: String(conductorIsolation.isolation?.color || '').toUpperCase(),
          }
        };

        updatedPhases[index].conductors.push(normalizedConductor);
        setConductorIsolationFormVisible(false);
        setConductorIsolation({
          wire: {},
          isolation: {}
        });
      }

      return {
        ...prev,
        phases: updatedPhases
      };
    });
  };

  const PHASE_DEPENDENCY = {
    pre_stranding: null,
    conductor_isolation: null, // or pre_stranding if it exists
    rope: 'conductor_isolation',
    interior_sheat: 'rope',
    armouring: 'interior_sheat',
    exterior_sheat: 'armouring' // or interior_sheat if no armouring
  };

  const addPhase = () => {
    if (!newPhase) return;

    let newPhaseData = { name: newPhase };

    // set dependsOn based on what phases already exist
    const existingPhaseNames = formData.phases.map(p => p.name);
    let dependsOn = PHASE_DEPENDENCY[newPhase] || null;

    // adjust dependsOn if the expected dependency doesn't exist
    if (dependsOn && !existingPhaseNames.includes(dependsOn)) {
      const PHASE_ORDER = ['pre_stranding', 'conductor_isolation', 'rope', 'interior_sheat', 'armouring', 'exterior_sheat'];
      const myIdx = PHASE_ORDER.indexOf(newPhase);
      dependsOn = null;
      for (let i = myIdx - 1; i >= 0; i--) {
        if (existingPhaseNames.includes(PHASE_ORDER[i])) {
          dependsOn = PHASE_ORDER[i];
          break;
        }
      }
    }
    newPhaseData.dependsOn = dependsOn;

    if (newPhase === 'pre_stranding') {
      newPhaseData.rope = [];
    } else if (newPhase === 'conductor_isolation') {
      newPhaseData.conductors = [];

      // FY/MYF: conductor_isolation is the last phase → set lastPhase
      const isSingleConductor = ['FY', 'MYF'].includes(formData.name.toUpperCase());
      if (isSingleConductor) {
        newPhaseData.lastPhase = true;
      }
    }

    setFormData(prev => ({
      ...prev,
      phases: [...prev.phases, newPhaseData]
    }));
  };

  const addPhaseComponent = (phaseName, index) => {
    if (phaseName === 'pre_stranding') {
      setPreStrandingFormVisible(true);
    }
    if (phaseName === 'conductor_isolation') {
      setConductorIsolationFormVisible(true);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      // ensure correct types before submit
      const submitData = {
        ...formData,
        hasMixedConductorSections: Boolean(formData.hasMixedConductorSections),
        numberOfConductors: Number(formData.numberOfConductors),
        section: Number(formData.section),
        greenYellowSection: formData.hasMixedConductorSections
          ? Number(formData.greenYellowSection)
          : null,
      };

      await axios.post(API_URL, submitData);

      // reset ALL state
      setFormData({
        name: '',
        numberOfConductors: '',
        section: '',
        hasMixedConductorSections: false,
        greenYellowSection: '',
        productStandard: '',
        performanceStandard: '',
        notifiedBody: '',
        dop: '',
        color: '',
        tension: '',
        fireReaction: '',
        phases: []
      });
      setNewPhase('');
      setPreStranding({});
      setPreStrandingFormVisible(true);
      setConductorIsolation({ wire: {}, isolation: {} });
      setConductorIsolationFormVisible(true);
      alert('New standard created successfully!');
    } catch (error) {
      console.error('Error creating standard:', error);
      alert('Failed to create standard.');
    }
  };

  const prediction = buildPrediction();
  const staleCount = countStalePhases(prediction);

  return (
    <Container className="mt-5">
      <Card className="p-4 shadow-sm">
        <Row className="align-items-center justify-content-between mb-4">
          <Col>
            <h2>Create New Standard</h2>
          </Col>
          <Col className="text-end">
            <Form.Check
              type="switch"
              id="hasMixedConductorSections"
              checked={formData.hasMixedConductorSections}
              onChange={e =>
                setFormData(prev => ({
                  ...prev,
                  hasMixedConductorSections: e.target.checked,
                  // Dacă e dezactivat, goliți greenYellowSection
                  greenYellowSection: e.target.checked
                    ? prev.greenYellowSection
                    : ''
                }))
              }
            />
          </Col>
        </Row>
        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={formData.hasMixedConductorSections ? 3 : 4}>
              <Form.Group controlId="name">
                <Form.Label>Nume</Form.Label>
                <Form.Select
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                >
                  <option value="">Alege tip cablu</option>
                  <option value="CYYF">CYYF</option>
                  <option value="CYABYF">CYABYF</option>
                  <option value="N2XH">N2XH</option>
                  <option value="MYYM">MYYM</option>
                  <option value="RVK">RVK</option>
                  <option value="MYYUP">MYYUP</option>
                  <option value="MYF">MYF</option>
                  <option value="FY">FY</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={formData.hasMixedConductorSections ? 3 : 4}>
              <Form.Group controlId="numberOfConductors">
                <Form.Label>Numar de conductori</Form.Label>
                <Form.Control
                  type="number"
                  name="numberOfConductors"
                  value={formData.numberOfConductors}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={formData.hasMixedConductorSections ? 3 : 4}>
              <Form.Group controlId="section">
                <Form.Label>Sectiune</Form.Label>
                <Form.Control
                  type="number"
                  name="section"
                  value={formData.section}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            {formData.hasMixedConductorSections && (
              <Col md={formData.hasMixedConductorSections ? 3 : 4}>
                <Form.Group controlId="greenYellowSection">
                  <Form.Label>Sectiune Verde-Galben</Form.Label>
                  <Form.Control
                    type="number"
                    name="greenYellowSection"
                    value={formData.greenYellowSection}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        greenYellowSection: Number(e.target.value)
                      }))
                    }
                    required
                  />
                </Form.Group>
              </Col>
            )}
          </Row>

          <Row className="g-3 mt-3">
            <Col md={6}>
              <Form.Group controlId="productStandard">
                <Form.Label>Standard Produs</Form.Label>
                <Form.Control
                  type="text"
                  name="productStandard"
                  value={formData.productStandard}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="performanceStandard">
                <Form.Label>Standard Performanta</Form.Label>
                <Form.Control
                  type="text"
                  name="performanceStandard"
                  value={formData.performanceStandard}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-3">
            <Col md={6}>
              <Form.Group controlId="notifiedBody">
                <Form.Label>Organism notificat</Form.Label>
                <Form.Control
                  type="text"
                  name="notifiedBody"
                  value={formData.notifiedBody}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="dop">
                <Form.Label>DOP</Form.Label>
                <Form.Control
                  type="text"
                  name="dop"
                  value={formData.dop}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mt-3">
            <Col md={4}>
              <Form.Group controlId="tension">
                <Form.Label>Tensiune</Form.Label>
                <Form.Control
                  type="text"
                  name="tension"
                  value={formData.tension}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="fireReaction">
                <Form.Label>Reactia la foc</Form.Label>
                <Form.Control
                  type="text"
                  name="fireReaction"
                  value={formData.fireReaction}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="color">
                <Form.Label>Color</Form.Label>
                <Form.Select
                  name="color"
                  value={formData.color}
                  onChange={handleChange}
                  required
                >
                  <option value="">
                    Selecteaza culoarea mantalei exterioare
                  </option>
                  <option value="WHITE">ALB</option>
                  <option value="BLACK">NEGRU</option>
                  <option value="GREY">GRI</option>
                  <option value="GREY 7004">GRI 7004</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <h4 className="mt-4">Faze de productie</h4>
          <Row className="g-3 mt-2">
            <Col md={8}>
              <Form.Group>
                <Form.Label>Selectati Faza</Form.Label>
                <Form.Select
                  value={newPhase}
                  onChange={e => setNewPhase(e.target.value)}
                >
                  <option value="">Selectati faza</option>
                  {PHASE_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button variant="primary" onClick={addPhase}>
                Add Phase
              </Button>
            </Col>
          </Row>

          {staleCount > 0 && (
            <Alert variant="warning" className="mt-3 d-flex justify-content-between align-items-center py-2">
              <span>
                <b>{staleCount}</b> {staleCount === 1 ? 'fază are' : 'faze au'} diametrul stocat incompatibil cu geometria curentă
                (s-a schimbat ceva upstream). Valorile nu au fost modificate automat.
              </span>
              <Button variant="warning" size="sm" onClick={applyAllPredictions}>
                Recalculează tot cablul
              </Button>
            </Alert>
          )}

          {formData.phases.map((phase, index) => (
            <Card className="mt-3 p-3" key={index}>
              <div className="d-flex justify-content-between align-items-center">
                <h5>
                  {PHASE_OPTIONS.find(p => p.value === phase.name)?.label ||
                    phase.name}
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleRemovePhase(index)}
                  >
                    <FontAwesomeIcon icon="fa-trash" />
                  </Button>
                </div>
              </div>

              {phase.name === 'pre_stranding' && (
                <>
                  {formData.phases[0].rope?.map((rope, ropeIndex) => (
                    <div
                      key={ropeIndex}
                      className="border p-3 mt-3 rounded bg-light"
                    >
                      <h5>Rope #{ropeIndex + 1}</h5>

                      <Row className="mb-2">
                        <Col md={4}>
                          <strong>Material:</strong> {rope.material}
                        </Col>
                        <Col md={4}>
                          <strong>Tip Fir:</strong> {rope.type}
                        </Col>
                        <Col md={4}>
                          <strong>Densitate:</strong> {rope.density}
                        </Col>
                      </Row>

                      <Row className="mb-2">
                        <Col md={4}>
                          <strong>Nr. Bobine:</strong> {rope.numberOfDrums}
                        </Col>
                        <Col md={4}>
                          <strong>Nr. Fire:</strong>{' '}
                          {Array.isArray(rope.numberOfWires)
                            ? rope.numberOfWires.join(', ')
                            : rope.numberOfWires}
                        </Col>
                        <Col md={4}>
                          <strong>Diametru Fire:</strong>{' '}
                          {Array.isArray(rope.wireDiameter)
                            ? rope.wireDiameter.join(', ')
                            : rope.wireDiameter}
                        </Col>
                      </Row>

                      {/* Optional: buton de șters dacă vrei */}
                      <div className="d-flex justify-content-end">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeRope(0, ropeIndex)}
                        >
                          <FontAwesomeIcon icon="fa-trash" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {preStandingFormVisible && (
                    <>
                      <Row>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Material fir</Form.Label>
                            <Form.Select
                              value={preStranding?.material || ''}
                              onChange={e => {
                                handlePreStrandingChange(
                                  'material',
                                  e.target.value
                                );
                              }}
                            >
                              <option>Alege material fir</option>
                              <option value="COPPER">Cupru</option>
                              <option value="aluminium">Aluminiu</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>

                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Tip Fir</Form.Label>
                            <Form.Select
                              value={preStranding?.type || ''}
                              onChange={e => {
                                handlePreStrandingChange(
                                  'type',
                                  e.target.value
                                );
                              }}
                            >
                              <option>Alege tip fir</option>
                              <option value="STRANDED">Trefilat</option>
                              <option value="MULTI-WIRE">
                                Multi-fir solid
                              </option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Densitate</Form.Label>
                            <Form.Control
                              type="number"
                              value={preStranding?.density || ''}
                              onChange={e => {
                                handlePreStrandingChange(
                                  'density',
                                  Number(e.target.value)
                                );
                              }}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      {preStranding.type?.toLowerCase() === 'stranded' && (
                        <Row>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Numar de bobine</Form.Label>
                              <Form.Control
                                type="number"
                                value={preStranding.numberOfDrums || ''}
                                onChange={e => {
                                  handlePreStrandingChange(
                                    'numberOfDrums',
                                    Number(e.target.value)
                                  );
                                }}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Numar de lite</Form.Label>
                              <Form.Control
                                type="number"
                                value={preStranding.numberOfWires || ''}
                                onChange={e => {
                                  handlePreStrandingChange(
                                    'numberOfWires',
                                    Number(e.target.value)
                                  );
                                }}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group>
                              <Form.Label>Diametru lita</Form.Label>
                              <Form.Control
                                type="number"
                                value={preStranding.wireDiameter || ''}
                                onChange={e => {
                                  handlePreStrandingChange(
                                    'wireDiameter',
                                    Number(e.target.value)
                                  );
                                }}
                                required
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}

                      {preStranding.type?.toLowerCase() === 'multi-wire' && (
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Numar de fire</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ex: 1,6"
                                value={preStranding.numberOfWires || ''}
                                onChange={e => {
                                  handlePreStrandingChange(
                                    'numberOfWires',
                                    e.target.value.split(',')
                                  );
                                }}
                                onBlur={e => {
                                  handlePreStrandingChange(
                                    'numberOfWires',
                                    e.target.value
                                      .split(',')
                                      .map(num => Number(num))
                                  );
                                }}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Diametru Fire</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ex: 1.35,1.70"
                                value={preStranding.wireDiameter || ''}
                                onChange={e => {
                                  handlePreStrandingChange(
                                    'wireDiameter',
                                    e.target.value
                                  );
                                }}
                                onBlur={e => {
                                  handlePreStrandingChange(
                                    'wireDiameter',
                                    e.target.value
                                      .split(',')
                                      .map(num => Number(num.trim()))
                                  );
                                }}
                                required
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => addPhaseComponent(phase.name)}
                    >
                      <FontAwesomeIcon icon="fa-solid fa-plus" />
                    </Button>
                    {preStandingFormVisible && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => savePhase(phase.name, index)}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-check" />
                      </Button>
                    )}
                  </div>
                </>
              )}

              {phase.name === 'conductor_isolation' && (
                <>
                  {phase.name === 'conductor_isolation' &&
                    Array.isArray(phase.conductors) && (
                      <>
                        {phase.conductors.map((conductor, cIdx) => (
                          <div
                            key={cIdx}
                            className="border p-3 mt-3 rounded bg-light"
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h5 className="mb-0">Conductor #{cIdx + 1}</h5>
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => removeConductor(index, cIdx)}
                              >
                                <FontAwesomeIcon icon="fa-trash" />
                              </Button>
                            </div>

                            <div className="row">
                              {/* WIRE SIDE */}
                              <div className="col-md-6">
                                <strong className="d-block mb-2">Wire</strong>
                                <ul className="mb-0">
                                  {Object.entries(conductor.wire || {}).map(
                                    ([k, v]) => (
                                      <li key={k}>
                                        <strong>
                                          {k.replace(/_/g, ' ').toUpperCase()}:
                                        </strong>{' '}
                                        {Array.isArray(v)
                                          ? v.join(', ')
                                          : typeof v === 'object' && v !== null
                                          ? JSON.stringify(v)
                                          : String(v ?? '-')}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>

                              {/* ISOLATION SIDE */}
                              <div className="col-md-6">
                                <strong className="d-block mb-2">
                                  Isolation
                                </strong>
                                <ul className="mb-0">
                                  {Object.entries(
                                    conductor.isolation || {}
                                  ).map(([k, v]) => {
                                    const isColor = k.toLowerCase() === 'color';
                                    return (
                                      <li
                                        key={k}
                                        className={
                                          isColor
                                            ? 'd-flex align-items-center gap-2'
                                            : ''
                                        }
                                      >
                                        <strong>
                                          {k.replace(/_/g, ' ').toUpperCase()}:
                                        </strong>{' '}
                                        {isColor ? (
                                          <>
                                            <span
                                              style={{
                                                backgroundColor: getColorHex(
                                                  String(v)
                                                ),
                                                width: 18,
                                                height: 18,
                                                display: 'inline-block',
                                                borderRadius: '50%',
                                                border: '1px solid #555'
                                              }}
                                              title={String(v)}
                                            />
                                            <span>{String(v ?? '-')}</span>
                                          </>
                                        ) : Array.isArray(v) ? (
                                          v.join(', ')
                                        ) : typeof v === 'object' &&
                                          v !== null ? (
                                          JSON.stringify(v)
                                        ) : (
                                          String(v ?? '-')
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                  {conductorIsolationFormVisible && (
                    <>
                      {' '}
                      <Row>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Material fir</Form.Label>
                            <Form.Select
                              value={conductorIsolation.wire?.material || ''}
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  wire: {
                                    ...prev.wire,
                                    material: e.target.value.toUpperCase()
                                  }
                                }))
                              }
                            >
                              <option>Alege material fir</option>
                              <option value="COPPER">Cupru</option>
                              <option value="ALUMINIUM">Aluminiu</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Tip Fir</Form.Label>
                            <Form.Select
                              value={conductorIsolation.wire?.type || ''}
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  wire: {
                                    ...prev.wire,
                                    type: e.target.value.toUpperCase()
                                  }
                                }))
                              }
                            >
                              <option>Alege tip fir</option>
                              <option value="STRANDED">Trefilat</option>
                              <option value="SINGLE-WIRE">Fir solid</option>
                              <option value="MULTI-WIRE">
                                Multi-fir solid
                              </option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Densitate</Form.Label>
                            <Form.Control
                              type="text"
                              value={conductorIsolation.wire?.density}
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  wire: {
                                    ...prev.wire,
                                    density: e.target.value
                                  }
                                }))
                              }
                              onBlur={e => {
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  wire: {
                                    ...prev.wire,
                                    density: Number(e.target.value)
                                  }
                                }));
                              }}
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      {conductorIsolation.wire?.type?.toLowerCase() ===
                        'single-wire' && (
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Numar de fire</Form.Label>
                              <Form.Control
                                type="number"
                                value={conductorIsolation.wire?.numberOfWires}
                                onChange={e =>
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      numberOfWires: Number(e.target.value)
                                    }
                                  }))
                                }
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Diametru Fir</Form.Label>
                              <Form.Control
                                type="number"
                                value={conductorIsolation.wire?.diameter}
                                required
                                onChange={e => {
                                  const diameter = Number(e.target.value);

                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      diameter
                                    }
                                  }));

                                  const section = calculateSection(diameter);

                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      section: section
                                    }
                                  }));
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Sectiune Fir</Form.Label>
                              <Form.Control
                                type="number"
                                value={conductorIsolation.wire?.section}
                                readOnly
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      {conductorIsolation.wire?.type?.toLowerCase() ===
                        'multi-wire' && (
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Numar de fire</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ex: 1,6"
                                required
                                value={
                                  conductorIsolation.wire?.numberOfWires || ''
                                }
                                onChange={e =>
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      numberOfWires: e.target.value
                                    }
                                  }))
                                }
                                onBlur={e => {
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      numberOfWires: e.target.value
                                        .split(',')
                                        .map(num => Number(num))
                                    }
                                  }));
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Diametru Fire</Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ex: 1.35,1.70"
                                value={
                                  conductorIsolation.wire?.wireDiameter || ''
                                }
                                onChange={e =>
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      wireDiameter: e.target.value
                                        .split(',')
                                        .map(num => num.trim())
                                    }
                                  }))
                                }
                                onBlur={e => {
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      wireDiameter: e.target.value
                                        .split(',')
                                        .map(num => Number(num.trim()))
                                    }
                                  }));

                                  const sectionFirstWire = calculateSection(
                                    Number(
                                      conductorIsolation.wire?.wireDiameter[0]
                                    )
                                  );
                                  const sectionSecondWire = calculateSection(
                                    Number(
                                      conductorIsolation.wire?.wireDiameter[1]
                                    )
                                  );

                                  const section =
                                    sectionFirstWire +
                                    sectionSecondWire *
                                      Number(
                                        conductorIsolation.wire
                                          ?.numberOfWires[1]
                                      );

                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      section: section
                                    }
                                  }));
                                }}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Diametru Funie ( CU/ AL)</Form.Label>
                              <Form.Control
                                type="number"
                                step="any"
                                value={conductorIsolation.wire?.diameter}
                                required
                                onChange={e => {
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      diameter: Number(e.target.value)
                                    }
                                  }));
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>FUNIE SECTIUNE</Form.Label>
                              <Form.Control
                                type="number"
                                value={conductorIsolation.wire?.section}
                                readOnly
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      {conductorIsolation.wire?.type?.toLowerCase() ===
                        'stranded' && (
                        <Row>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Numar de lite</Form.Label>
                              <Form.Control
                                type="number"
                                step={'any'}
                                value={
                                  conductorIsolation.wire?.numberOfWires || ''
                                }
                                onChange={e =>
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      numberOfWires: Number(e.target.value)
                                    }
                                  }))
                                }
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Diametru lita</Form.Label>
                              <Form.Control
                                type="text"
                                value={
                                  conductorIsolation.wire?.wireDiameter || ''
                                }
                                onChange={e =>
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      wireDiameter: e.target.value
                                    }
                                  }))
                                }
                                onBlur={e => {
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      wireDiameter: Number(e.target.value)
                                    }
                                  }));

                                  const wd = Number(conductorIsolation.wire?.wireDiameter);
                                  const nw = Number(conductorIsolation.wire?.numberOfWires);
                                  const section = ((Math.PI * wd * wd) / 4) * nw;

                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      section: section
                                    }
                                  }));
                                }}
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Diametru funie lite</Form.Label>
                              <Form.Control
                                type="text"
                                step="any"
                                value={conductorIsolation.wire?.diameter}
                                required
                                onChange={e => {
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      diameter: e.target.value.trim()
                                    }
                                  }));
                                }}
                                onBlur={e => {
                                  setConductorIsolation(prev => ({
                                    ...prev,
                                    wire: {
                                      ...prev.wire,
                                      diameter: Number(e.target.value)
                                    }
                                  }));
                                }}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group>
                              <Form.Label>Sectiune funie lite</Form.Label>
                              <Form.Control
                                type="number"
                                value={conductorIsolation.wire?.section}
                                readOnly
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      <h6 className="mt-3">Material Izolatie</h6>
                      <Row>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Material</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Ex: PVC, POLIETILEN"
                              value={
                                conductorIsolation.isolation?.material || ''
                              }
                              required
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    material: e.target.value.toUpperCase()
                                  }
                                }))
                              }
                            />
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Tip</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="Ex: SDR , NATUR , LE4442"
                              value={conductorIsolation.isolation?.type || ''}
                              required
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    type: e.target.value.toUpperCase()
                                  }
                                }))
                              }
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Densitate</Form.Label>
                            <Form.Control
                              type="text"
                              // placeholder="Ex: SDR , NATUR , LE4442"
                              value={
                                conductorIsolation.isolation?.density || ''
                              }
                              required
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    density: e.target.value.trim()
                                  }
                                }))
                              }
                              onBlur={e => {
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    density: Number(e.target.value)
                                  }
                                }));
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>
                              Diametru
                              {(() => {
                                const est = estimateIsolationDiameter(
                                  conductorIsolation.wire?.diameter,
                                  conductorIsolation.isolation?.minRadial,
                                  conductorIsolation.isolation?.maxRadial
                                );
                                if (!est) return null;
                                return (
                                  <Button size="sm" variant="outline-success" className="ms-2 py-0 px-1" style={{ fontSize: 10 }}
                                    onClick={() => {
                                      const d = est;
                                      const section = calculateSection(d);
                                      const wireSection = Number(conductorIsolation.wire?.section) || 0;
                                      const fillingSection = Number((section - wireSection).toFixed(4));
                                      setConductorIsolation(prev => ({
                                        ...prev,
                                        isolation: { ...prev.isolation, diameter: d, section, fillingSection }
                                      }));
                                    }}
                                  >
                                    Est: {est} mm
                                  </Button>
                                );
                              })()}
                            </Form.Label>
                            <Form.Control
                              type="number"
                              step="any"
                              value={conductorIsolation.isolation?.diameter || ''}
                              required
                              onChange={e => {
                                const d = Number(e.target.value);
                                const section = calculateSection(d);
                                const wireSection = Number(conductorIsolation.wire?.section) || 0;
                                const fillingSection = Number((section - wireSection).toFixed(4));
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    diameter: d,
                                    section,
                                    fillingSection
                                  }
                                }));
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Sectiune</Form.Label>
                            <Form.Control
                              type="number"
                              // placeholder="Ex: SDR , NATUR , LE4442"
                              value={
                                conductorIsolation.isolation?.section || ''
                              }
                              readOnly
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Sectiune de umplere</Form.Label>
                            <Form.Control
                              type="number"
                              // placeholder="Ex: SDR , NATUR , LE4442"
                              value={
                                conductorIsolation.isolation?.fillingSection ||
                                ''
                              }
                              readOnly
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>RADIAL MINIM STD</Form.Label>
                            <Form.Control
                              required
                              type="text"
                              // placeholder="Ex: SDR , NATUR , LE4442"
                              value={
                                conductorIsolation.isolation?.minRadial || ''
                              }
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    minRadial: e.target.value.trim()
                                  }
                                }))
                              }
                              onBlur={e => {
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    minRadial: Number(e.target.value)
                                  }
                                }));
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>RADIAL MAXIM STD</Form.Label>
                            <Form.Control
                              required
                              type="text"
                              // placeholder="Ex: SDR , NATUR , LE4442"
                              value={
                                conductorIsolation.isolation?.maxRadial || ''
                              }
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    maxRadial: e.target.value.trim()
                                  }
                                }))
                              }
                              onBlur={e => {
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    maxRadial: Number(e.target.value)
                                  }
                                }));
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group>
                            <Form.Label>Izolatie Culoare</Form.Label>
                            <Form.Select
                              required
                              value={conductorIsolation.isolation?.color || ''}
                              onChange={e =>
                                setConductorIsolation(prev => ({
                                  ...prev,
                                  isolation: {
                                    ...prev.isolation,
                                    color: e.target.value
                                  }
                                }))
                              }
                            >
                              <option value="">Alege culoare</option>
                              <option value="GREEN-YELLOW">VERDE-GALBEN</option>
                              <option value="BROWN">MARO</option>
                              <option value="BLUE">ALBASTRU</option>
                              <option value="BLACK">NEGRU</option>
                              <option value="GREY">GRI</option>
                              <option value="RED">ROSU</option>
                              <option value="WHITE">ALB</option>
                            </Form.Select>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}

                  <div className="d-flex justify-content-end gap-2 mt-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => addPhaseComponent(phase.name, index)}
                    >
                      <FontAwesomeIcon icon="fa-solid fa-plus" />
                    </Button>
                    {conductorIsolationFormVisible && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => savePhase(phase.name, index)}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-check" />
                      </Button>
                    )}
                  </div>
                </>
              )}

              {phase.name === 'rope' && (() => {
                const pred = prediction.pred[index];
                return (
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Diametru Funie</Form.Label>
                        <Form.Control type="number" step="any" value={phase.diameter || ''} required onChange={e => {
                          const d = Number(e.target.value);
                          const section = calculateSection(d);
                          const phases = [...formData.phases];
                          phases[index] = { ...phases[index], diameter: d, section };
                          setFormData(prev => ({ ...prev, phases }));
                        }} />
                        <PhasePrediction stored={phase.diameter} pred={pred} onAccept={() => applyPrediction(index)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sectiune Funie</Form.Label>
                        <Form.Control type="number" step="any" value={phase.section || ''} onChange={e => handlePhaseChange(index, 'section', e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                );
              })()}

              {phase.name === 'interior_sheat' && (() => {
                return (
                  <Row>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Material</Form.Label>
                        <Form.Control type="text" required placeholder="Ex: PVC" value={phase.material || ''} onChange={e => handlePhaseChange(index, 'material', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Tip</Form.Label>
                        <Form.Control type="text" required placeholder="Ex: SDR, NATUR" value={phase.type || ''} onChange={e => handlePhaseChange(index, 'type', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Densitate</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.density || ''} onChange={e => handlePhaseChange(index, 'density', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Radial Minim</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.minRadial || ''} onChange={e => handlePhaseChange(index, 'minRadial', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Radial Maxim</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.maxRadial || ''} onChange={e => handlePhaseChange(index, 'maxRadial', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Diametru</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.diameter || ''} onChange={e => {
                          const d = Number(e.target.value);
                          const { section, fillingSection } = computePhaseDerived(index, d);
                          const phases = [...formData.phases];
                          phases[index] = { ...phases[index], diameter: d, section, fillingSection };
                          setFormData(prev => ({ ...prev, phases }));
                        }} />
                        <PhasePrediction stored={phase.diameter} pred={prediction.pred[index]} onAccept={() => applyPrediction(index)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Sectiune</Form.Label>
                        <Form.Control type="number" step="any" value={phase.section || ''} onChange={e => handlePhaseChange(index, 'section', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Filling Section</Form.Label>
                        <Form.Control type="number" step="any" value={phase.fillingSection || ''} onChange={e => handlePhaseChange(index, 'fillingSection', e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                );
              })()}

              {phase.name === 'armouring' && (() => {
                return (
                  <Row>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Latime BLR (mm)</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.wide || ''} onChange={e => handlePhaseChange(index, 'wide', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Grosime BLR (mm)</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.thickness || ''} onChange={e => handlePhaseChange(index, 'thickness', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Densitate BLR</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.density || ''} onChange={e => handlePhaseChange(index, 'density', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Diametru Cablu</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.diameter || ''} onChange={e => {
                          const d = Number(e.target.value);
                          const section = calculateSection(d);
                          const phases = [...formData.phases];
                          phases[index] = { ...phases[index], diameter: d, section };
                          setFormData(prev => ({ ...prev, phases }));
                        }} />
                        <PhasePrediction stored={phase.diameter} pred={prediction.pred[index]} onAccept={() => applyPrediction(index)} />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Sectiune</Form.Label>
                        <Form.Control type="number" step="any" value={phase.section || ''} onChange={e => handlePhaseChange(index, 'section', e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                );
              })()}

              {phase.name === 'exterior_sheat' && (() => {
                return (
                  <Row>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Material</Form.Label>
                        <Form.Control type="text" required placeholder="Ex: PVC" value={phase.material || ''} onChange={e => handlePhaseChange(index, 'material', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Tip</Form.Label>
                        <Form.Control type="text" required placeholder="Ex: NEGRU, GRI" value={phase.type || ''} onChange={e => handlePhaseChange(index, 'type', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Densitate</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.density || ''} onChange={e => handlePhaseChange(index, 'density', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Radial Minim</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.minRadial || ''} onChange={e => handlePhaseChange(index, 'minRadial', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Radial Maxim</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.maxRadial || ''} onChange={e => handlePhaseChange(index, 'maxRadial', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Diametru</Form.Label>
                        <Form.Control type="number" step="any" required value={phase.diameter || ''} onChange={e => {
                          const d = Number(e.target.value);
                          const { section, fillingSection } = computePhaseDerived(index, d);
                          const phases = [...formData.phases];
                          phases[index] = { ...phases[index], diameter: d, section, fillingSection };
                          setFormData(prev => ({ ...prev, phases }));
                        }} />
                        <PhasePrediction stored={phase.diameter} pred={prediction.pred[index]} onAccept={() => applyPrediction(index)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Sectiune</Form.Label>
                        <Form.Control type="number" step="any" value={phase.section || ''} onChange={e => handlePhaseChange(index, 'section', e.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Filling Section</Form.Label>
                        <Form.Control type="number" step="any" value={phase.fillingSection || ''} onChange={e => handlePhaseChange(index, 'fillingSection', e.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                );
              })()}
            </Card>
          ))}

          <div className="mt-4 text-end">
            <Button type="submit" variant="primary" className="ms-3">
              Create Standard
            </Button>
          </div>
        </Form>
      </Card>
    </Container>
  );
};

export default CreateNewStandard;
